import { useRef, useState, useEffect, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Cell,
} from "recharts";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const Charts = ({
  objectiveName,
  allMetric,
  metricsCount,
  objectivesCount,
  greenMeticsCount,
  yellowMeticsCount,
  redMeticsCount,
  dateRangeData,
  isDataLoading,
  onFrequencyChange,
  activeMetric: controlledMetric,
  onMetricChange,
  onDataChange,
  dateRange,
}) => {
  const [activeMetric, setActiveMetric] = useState(
    controlledMetric
  );

  // Determine if the selected range is one month or less
  const isOneMonthOrLess = useMemo(() => {
    console.log("diffDays",dateRange);
    if (!dateRange || dateRange.length !== 2) return false;
    const [startDate, endDate] = dateRange;
    if (!startDate || !endDate) return false;

    // Calculate the difference in days
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Check if we're dealing with a single month range
    const isSameMonth = startDate.getMonth() === endDate.getMonth() &&
                       startDate.getFullYear() === endDate.getFullYear();

    console.log("diffDays",diffDays);
    // Return true if within 31 days OR if it's the same month
    return diffDays <= 31 || isSameMonth;
  }, [dateRange]); 

  const [frequency, setFrequency] = useState("weekly");
  const [showFrequencyDropdown, setShowFrequencyDropdown] = useState(false);
  const [showScrollButtons, setShowScrollButtons] = useState(false); 

  // const data = [
  //   { date: "01 Aug", target: 11, result: 30, risk: "green" },
  //   { date: "02 Aug", target: 34, result: 34, risk: "amber" },
  // ];

  const data = useMemo(() => {
    return dateRangeData.flatMap(sectionData =>
      sectionData.metrics?.filter(metric => activeMetric.id == metric.id).map(metric => ({
        date: sectionData.section || 'N/A',
        target: metric.target?.green_level || 0,
        result: metric.avgValue || 0,
        risk: metric.riskLevel === 'yellow' ? 'amber' : metric.riskLevel || 'red',
      })) || []
    );
  }, [dateRangeData, activeMetric.id]);

  const filtersRef = useRef(null);
  const wrapperRef = useRef(null);
  const scrollBy = (delta) => {
    if (filtersRef.current) {
      filtersRef.current.scrollBy({ left: delta, behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (controlledMetric && controlledMetric !== activeMetric) {
      setActiveMetric(controlledMetric);
    }
  }, [controlledMetric]);

  useEffect(() => {
    if (typeof onMetricChange === "function") {
      onMetricChange(activeMetric);
    }
  }, [activeMetric]);

  useEffect(() => {
    const defaultFrequency = isOneMonthOrLess ? "weekly" : "daily";
    setFrequency(defaultFrequency);
    if (typeof onFrequencyChange === "function") {
      onFrequencyChange(defaultFrequency);
    }
  }, [isOneMonthOrLess]);

  // Check if scrolling is needed for metrics
  useEffect(() => {
    const checkScrollNeeded = () => {
      if (filtersRef.current && wrapperRef.current) {
        const scrollContainer = filtersRef.current;
        const wrapper = wrapperRef.current;

        // Check if content width exceeds container width
        const isScrollNeeded = scrollContainer.scrollWidth > scrollContainer.clientWidth;
        setShowScrollButtons(isScrollNeeded);
      }
    };

    // Check on mount and when allMetric changes
    checkScrollNeeded();

    // Add resize listener
    window.addEventListener('resize', checkScrollNeeded);

    return () => {
      window.removeEventListener('resize', checkScrollNeeded);
    };
  }, [allMetric]);

  const handleFrequencyChange = (newFrequency) => {
    setFrequency(newFrequency);
    setShowFrequencyDropdown(false);
    if (typeof onFrequencyChange === "function") {
      onFrequencyChange(newFrequency);
    }
  };

  const getAvailableFrequencies = () => {
    if (isOneMonthOrLess) {
      return [
        { value: "daily", label: "Daily" },
        { value: "weekly", label: "Weekly" }
      ];
    }
    return [];
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const row = payload[0].payload;
      const riskMap = {
        green: { color: "#68C348", label: "Green" },
        amber: { color: "#FBC207", label: "Amber" },
        red: { color: "#F52905", label: "Red" },
      };
      const risk = riskMap[row.risk] || riskMap.amber;
      return (
        <div className={`chart-tooltip chart-tooltip--${row.risk}`}>
          <div className="tooltip-content">
            <div
              className="tooltip-dot"
              style={{ background: risk.color }}
            ></div>
            <div className="tooltip-text">{risk.label}</div>
          </div>
          <div className="tooltip-date">{label.replace("\n", " ")} 2025</div>
          <div className="tooltip-values">
            <div className="tooltip-value">
              <div className="tooltip-value-text">Target: {row.target}</div>
            </div>
            <div className="tooltip-value">
              <div className="tooltip-value-text">Result: {row.result}</div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomBar = (props) => {
    const { fill, payload, x, y, width, height } = props;
    const colors = {
      green: ["#072837", "#68C348"],
      amber: ["#072837", "#FBC207"],
      red: ["#072837", "#F52905"],
    };

    const [targetColor, resultColor] = colors[payload.risk] || [
      "#072837",
      "#68C348",
    ];

    return (
      <g>
        {/* Target bar */}
        <rect
          x={x}
          y={y + height - (payload.target / 50) * height}
          width={width / 2 - 2}
          height={(payload.target / 50) * height}
          fill={targetColor}
          rx={2}
        />
        {/* Result bar */}
        <rect
          x={x + width / 2 + 2}
          y={y + height - (payload.result / 50) * height}
          width={width / 2 - 2}
          height={(payload.result / 50) * height}
          fill={resultColor}
          rx={2}
        />
      </g>
    );
  };

  return (
    <>
      <div className="risk-analysis-title">
        Risk Level Analysis for Selected Date Range
      </div>

      {isOneMonthOrLess && (<div className="frequency-controls">
        <div className="frequency-label">Frequency:</div>
        <div className="frequency-picker" style={{ position: 'relative' }}>
          <div
            className="frequency-button"
            onClick={() => setShowFrequencyDropdown(!showFrequencyDropdown)}
          >
            <Calendar size={14} className="frequency-icon" />
            <span>{frequency.charAt(0).toUpperCase() + frequency.slice(1)}</span>
            <svg
              className={`frequency-arrow ${showFrequencyDropdown ? 'open' : ''}`}
              width="8"
              height="5"
              viewBox="0 0 8 5"
              fill="none"
            >
              <path d="M1 1L4 4L7 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          {showFrequencyDropdown && (
            <div
              className="frequency-dropdown"
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                zIndex: 1000,
                marginTop: '2px'
              }}
            >
              {getAvailableFrequencies().map(option => (
                <div
                  key={option.value}
                  className={`frequency-option ${frequency === option.value ? 'active' : ''}`}
                  onClick={() => handleFrequencyChange(option.value)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: frequency === option.value ? '#072837' : '#666',
                    backgroundColor: frequency === option.value ? '#f5f5f5' : 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (frequency !== option.value) {
                      e.target.style.backgroundColor = '#f9f9f9';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (frequency !== option.value) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {option.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>)}
      {isDataLoading ? (
        <div className="metric-filters-wrapper">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="metric-filter-pill">
              <Skeleton
                width={80}
                height={16}
                baseColor="#f3f3f3"
                highlightColor="#ecebeb"
              />
            </div>
          ))}
          </div>
        ):(
          <div className="metric-filters-wrapper" ref={wrapperRef}>
            {showScrollButtons && (
              <button
                className="metric-filter-arrow"
                onClick={() => scrollBy(-220)}
                aria-label="Scroll left"
              >
                <ChevronLeft size={14} color="#FFF" />
              </button>
            )}
            <div className="metric-filters-scroll" ref={filtersRef}>
              {allMetric.map((metric) => (
                <div
                  key={metric}
                  className={`metric-filter-pill ${
                    activeMetric.id == metric.id ? "active" : ""
                  }`}
                  onClick={() => setActiveMetric(metric)}
                >
                  {metric.name}
                </div>
              ))}
            </div>
            {showScrollButtons && (
              <button
                className="metric-filter-arrow"
                onClick={() => scrollBy(220)}
                aria-label="Scroll right"
              >
                <ChevronRight size={14} color="#FFF" />
              </button>
            )}
          </div>
        )}

      <div className="chart-legend" style={{ marginTop: 8 }}>
        <div className="legend-item">
          <div className="legend-dot target"></div>
          <div className="legend-text">Target</div>
        </div>
        <div className="legend-item">
          <div className="legend-dot green"></div>
          <div className="legend-text">Green</div>
        </div>
        <div className="legend-item">
          <div className="legend-dot amber"></div>
          <div className="legend-text">Amber</div>
        </div>
        <div className="legend-item">
          <div className="legend-dot red"></div>
          <div className="legend-text">Red</div>
        </div>
      </div>

      <div className="chart-section">
        <div className="chart-main" style={{ width: "100%" }}>
          <div className="chart-area">
            <div className="chart-plot" style={{ width: "100%" }}>
              <div
                className="chart-scroll-container"
                style={{
                  height: 300,
                  width: "100%",
                  overflowX: "auto",
                  overflowY: "hidden",
                }}
              >
                {isDataLoading ? (
                  <div className="chart-skeleton" style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-around",
                    padding: "20px",
                    gap: "10px"
                  }}>
                    {[...Array(12)].map((_, index) => (
                      <div key={index} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                        {/* Skeleton bars */}
                        <div style={{
                          display: "flex",
                          gap: "2px",
                          alignItems: "flex-end"
                        }}>
                          <Skeleton
                            width={8}
                            height={Math.random() * 100 + 50}
                            borderRadius="32px 32px 0 0"
                            baseColor="#f3f3f3"
                            highlightColor="#ecebeb"
                          />
                          <Skeleton
                            width={8}
                            height={Math.random() * 120 + 30}
                            borderRadius="32px 32px 0 0"
                            baseColor="#f3f3f3"
                            highlightColor="#ecebeb"
                          />
                        </div>
                        {/* Skeleton x-axis label */}
                        <Skeleton
                          width={40}
                          height={10}
                          borderRadius="2px"
                          baseColor="#f3f3f3"
                          highlightColor="#ecebeb"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      minWidth: `${Math.max(800, data.length * 25)}px`,
                      height: "100%",
                    }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={data}
                        margin={{ top: 0, right: 10, left: 0, bottom: 20 }}
                        barCategoryGap="20%"
                      >
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 10, fill: "#686868" }}
                          axisLine={true}
                          tickLine={false}
                          height={40}
                        />
                        <YAxis
                          tick={{ fontSize: 10, fill: "#686868" }}
                          axisLine={true}
                          tickLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar
                          dataKey="target"
                          fill="#072837"
                          barSize={8}
                          radius={[32, 32, 0, 0]}
                        />
                        <Bar dataKey="result" barSize={8} radius={[32, 32, 0, 0]}>
                          {data.map((row, idx) => (
                            <Cell
                              key={`result-${idx}`}
                              fill={
                                row.risk === "green"
                                  ? "#68C348"
                                  : row.risk === "red"
                                  ? "#F52905"
                                  : "#FBC207"
                              }
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Charts;
